import React, { FC, ReactNode } from "react";
import { useRouter } from "next/router";
import { ChevronIconSvg } from "../../icons";
import { Form } from "react-final-form";
import { FieldInput } from "../../fields/FieldInput";
import { Button } from "../../Button";
import { Heading } from "../../Heading";
import { TermsAndConditions } from "../../TermsAndConditions";
import { BrandConfigCustomizationsImage } from "../../../types/graphql/generated";
import { validateWithYup, loginSchema } from "../../../helpers/validation";
import { formFields } from "../../../constants";
import {
  StyledAccountLogo,
  StyledError,
  StyledForm,
  StyledLoginButton,
  StyledLoginForm,
  StyledLogo,
  StyledResetPassword,
} from "./index.styled";
import styled from "styled-components";

interface LoginFormProps {
  children?: ReactNode;
  error: string;
  fallbackUrl?: string;
  goToForgotPassword: () => void;
  isLoading: boolean;
  logoSrc?: string;
  accountLogo?: BrandConfigCustomizationsImage;
  onSubmit: (formValues: LoginFormValues) => void;
  privacyUrl?: string;
  termsUrl?: string;
  signinPageTitle?: string | null;
}

export interface LoginFormValues {
  email: string;
  password: string;
}

const LinkWrapper = styled.div`
  font-size: 40px;
  margin-bottom: 20px;
  > :hover {
    cursor: pointer;
  }
`;

export const LoginForm: FC<LoginFormProps> = ({
  accountLogo,
  children,
  error,
  fallbackUrl,
  goToForgotPassword,
  isLoading,
  logoSrc,
  onSubmit,
  termsUrl,
  signinPageTitle,
}) => {
  const router = useRouter();
  return (
    <StyledLoginForm>
      {!!logoSrc && <StyledLogo src={logoSrc} alt="shop logo" />}

      {!!accountLogo && (
        <div>
          <LinkWrapper
            onClick={() => {
              router.back();
            }}
          >
            <ChevronIconSvg />
          </LinkWrapper>
          <StyledAccountLogo src={accountLogo.src} alt="Account" />
        </div>
      )}

      <Heading level="h2" size="l">
        {signinPageTitle}
      </Heading>
      <Form
        onSubmit={(formValues: LoginFormValues) => onSubmit(formValues)}
        validate={validateWithYup(loginSchema)}
        render={({ handleSubmit }) => (
          <StyledForm onSubmit={handleSubmit}>
            <FieldInput
              {...formFields.email}
              placeholder="Enter email address"
            />
            <FieldInput
              {...formFields.password}
              type="password"
              placeholder="Enter password"
            />
            <TermsAndConditions termsUrl={termsUrl || ""} type="info">
              <StyledLoginButton>
                <Button htmlType="submit" loading={isLoading} fullWidth>
                  Login
                </Button>
              </StyledLoginButton>
              <StyledResetPassword>
                <Button type="text" onClick={goToForgotPassword}>
                  Forgot your password?
                </Button>
              </StyledResetPassword>
            </TermsAndConditions>
            {error && <StyledError>{error}</StyledError>}
          </StyledForm>
        )}
      />
      {children}
    </StyledLoginForm>
  );
};
